import React from 'react';
import { Box, Typography, Card, Button, useTheme } from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CodeIcon from '@mui/icons-material/Code';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TerminalIcon from '@mui/icons-material/Terminal';
import { playClickSound } from '../../utils/soundEffects';

const HeroTelemetryHUD = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const telemetryItems = [
    {
      label: 'CODEFORCES',
      val: '2013 (Candidate Master)',
      color: '#34d399',
      sub: 'Top 500 Global Rank',
      icon: <CodeIcon sx={{ fontSize: 16, color: '#34d399' }} />
    },
    {
      label: 'ALGORITHMIC RIGOR',
      val: '1,000+ Solved',
      color: '#fbbf24',
      sub: 'DP, Trees & Graphs',
      icon: <TerminalIcon sx={{ fontSize: 16, color: '#fbbf24' }} />
    },
    {
      label: 'LIBRARY ARCHIVE',
      val: '15+ Classical Volumes',
      color: '#c084fc',
      sub: 'Statecraft, History & CS',
      icon: <AutoStoriesIcon sx={{ fontSize: 16, color: '#c084fc' }} />
    },
    {
      label: 'CORE SYSTEMS STACK',
      val: 'C/C++20 · POSIX · React',
      color: '#38bdf8',
      sub: 'Low-Level & Full-Stack',
      icon: <TerminalIcon sx={{ fontSize: 16, color: '#38bdf8' }} />
    }
  ];

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: '18px', md: '24px' },
        background: isDark
          ? 'linear-gradient(135deg, rgba(229, 169, 60, 0.08) 0%, rgba(13, 17, 26, 0.95) 50%, rgba(15, 23, 42, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(184, 134, 11, 0.06) 0%, #ffffff 50%, rgba(248, 245, 238, 0.98) 100%)',
        border: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.35)' : 'rgba(184, 134, 11, 0.25)'}`,
        boxShadow: isDark
          ? '0 20px 50px rgba(0, 0, 0, 0.75), 0 0 35px rgba(229, 169, 60, 0.12)'
          : '0 12px 32px rgba(0, 0, 0, 0.08), 0 0 20px rgba(184, 134, 11, 0.08)',
        backdropFilter: 'blur(20px)',
        mb: 6,
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2.5, lg: 3 }
      }}
    >
      {/* 4 Telemetry Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          flexGrow: 1
        }}
      >
        {telemetryItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              p: { xs: 1.5, sm: 1.8 },
              borderRadius: '14px',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `3.5px solid ${item.color}`,
              transition: 'all 0.25s ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
              {item.icon}
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.64rem',
                  fontWeight: 800,
                  color: theme.palette.text.secondary,
                  letterSpacing: '0.06em'
                }}
              >
                {item.label}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.94rem', sm: '1rem' },
                color: item.color,
                lineHeight: 1.25,
                mb: 0.2
              }}
            >
              {item.val}
            </Typography>

            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.68rem',
                color: theme.palette.text.secondary,
                fontWeight: 500
              }}
            >
              {item.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Recruiter Instant Briefing Launcher */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'stretch', lg: 'flex-end' },
          gap: 1,
          pl: { xs: 0, lg: 2 },
          borderLeft: { xs: 'none', lg: `1px solid ${theme.palette.divider}` },
          pt: { xs: 1.5, lg: 0 },
          borderTop: { xs: `1px solid ${theme.palette.divider}`, lg: 'none' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', lg: 'flex-end' } }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#34d399',
              boxShadow: '0 0 10px #34d399',
              animation: 'radarPulse 2s infinite ease-in-out',
              '@keyframes radarPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.4, transform: 'scale(1.2)' }
              }
            }}
          />
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.72rem',
              fontWeight: 800,
              color: isDark ? '#34d399' : '#059669',
              letterSpacing: '0.04em'
            }}
          >
            OPEN TO HIGH-IMPACT ROLES
          </Typography>
        </Box>

        <Button
          onClick={() => {
            playClickSound();
            window.dispatchEvent(new CustomEvent('open-recruiter-modal'));
          }}
          variant="contained"
          size="small"
          startIcon={<FlashOnIcon sx={{ fontSize: 16 }} />}
          sx={{
            background: 'linear-gradient(135deg, #e5a93c 0%, #d97706 100%)',
            color: '#0a0c10',
            fontWeight: 800,
            fontSize: '0.82rem',
            px: 2.2,
            py: 0.9,
            borderRadius: '10px',
            textTransform: 'none',
            boxShadow: '0 4px 16px rgba(229, 169, 60, 0.35)',
            whiteSpace: 'nowrap',
            '&:hover': {
              background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 22px rgba(229, 169, 60, 0.5)'
            }
          }}
        >
          Open 30s Executive Dossier
        </Button>
      </Box>
    </Card>
  );
};

export default HeroTelemetryHUD;
